import { useEffect } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { captureError } from '@leagueplatform/observability';
import {
  API_JSON_CONTENT_TYPE,
  createEndpointFetch,
} from '@web-config-app/api';
import type {
  PermissionsData,
  PermissionsHookProps,
  PermissionsResponse,
} from '@web-config-app/core';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';

const PERMISSIONS_DATA_QUERY_KEY = 'getPermissions';

type PermissionsUseQueryResult = UseQueryResult<PermissionsResponse, Error>;

export type UseGetPermissionsResult = Omit<
  PermissionsUseQueryResult,
  'data'
> & {
  /**
   * There's a clash between `useQuery` returning a `data` property and our endpoints that ALSO include a `data` property
   * in the response. So, to avoid have `data.data`, hoist the endpoint's `response.data` to the top level here.
   */
  data?: PermissionsData;
};

interface UseGetPermissionsProps extends PermissionsHookProps {}

/**
 * This hook is used to fetch the permissions for the current user using React Query.
 *
 * @param props.environmentKey - The environment to fetch permissions from
 * @param props.tenantId - The tenant ID for cache isolation (ensures permissions are refetched on tenant switch)
 * @param props.isAuthenticated - Whether the user is authenticated (enables the query when true)
 * @returns {UseGetPermissionsResult} The React Query result object with:
 * - `data` - The user's permissions data (hoisted from `response.data` to avoid `data.data`)
 * - `isLoading`, `isError`, `status`, etc. - Standard React Query state properties
 */
export const useGetPermissions = ({
  environmentKey,
  tenantId,
  isAuthenticated,
}: UseGetPermissionsProps): UseGetPermissionsResult => {
  const headers = {
    'Content-Type': API_JSON_CONTENT_TYPE,
  };

  const path = '/v1/permissions';

  const environmentFetch = useEnvironmentFetch(environmentKey);

  const queryFn = async () => {
    const endpointConfig = {
      path,
      method: 'GET',
    } as const;

    const permissionsFetch = createEndpointFetch<PermissionsResponse>(
      endpointConfig,
      environmentFetch,
    );

    return permissionsFetch({
      headers,
    });
  };

  const isEnabled = isAuthenticated && Boolean(tenantId);

  const {
    data: queryResultData,
    error,
    isError,
    ...queryResult
  } = useQuery<PermissionsResponse, Error>({
    queryKey: [PERMISSIONS_DATA_QUERY_KEY, path, environmentKey, tenantId],
    queryFn,
    staleTime: Infinity,
    retry: 2, // Maybe we want to retry for a certain number of times?
    throwOnError: false,
    enabled: isEnabled,
  });

  useEffect(() => {
    if (isError && error) {
      // eslint-disable-next-line no-console
      console.error('[useGetPermissions] Error', error);
      captureError(
        new Error(`Failed to retrieve permissions for this user: ${error}`),
      );
    }
  }, [isError, error]);

  return {
    ...queryResult,
    error,
    isError,
    data: queryResultData?.data,
  };
};
